import { useState } from "react";
import { Mail, MessageSquareText, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import TurnstileWidget from "@/components/site/TurnstileWidget";

const topics = ["Sales enquiry", "Support", "Partnerships", "Press", "Other"];

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", topic: "Sales enquiry", message: "" });
    const [token, setToken] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", { ...form, turnstile_token: token || undefined });
            setSubmitted(true);
            toast.success("Message sent — we'll be back to you within one business day.");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send message. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="min-h-screen flex flex-col bg-white">
            <div className="bg-white border-b border-zinc-200">
                <Navbar tone="light" />
            </div>

            <section className="bg-gradient-to-b from-zinc-50 to-white border-b border-zinc-200">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-20">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Contact</p>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-900">
                        Talk to a human at PipeGuide.
                    </h1>
                    <p className="mt-4 text-lg text-zinc-600 max-w-xl">
                        Sales questions, custom-pricing chats, partnership ideas — we read every message and reply within one business day.
                    </p>
                </div>
            </section>

            <section className="bg-white flex-1">
                <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <aside className="lg:col-span-4 space-y-6">
                        <div className="rounded-2xl border border-zinc-200 bg-zinc-50/50 p-6">
                            <Mail className="h-5 w-5 text-brand-primary" />
                            <h3 className="mt-4 font-display text-lg font-semibold text-zinc-900">Email</h3>
                            <p className="mt-2 text-sm text-zinc-600">For sales, support and everything in between.</p>
                            <a href="mailto:support@support.pipeguide.tech" className="mt-3 inline-block text-sm font-medium text-brand-primary hover:underline">
                                support@support.pipeguide.tech
                            </a>
                        </div>
                        <div className="rounded-2xl border border-zinc-200 bg-zinc-50/50 p-6">
                            <MessageSquareText className="h-5 w-5 text-brand-primary" />
                            <h3 className="mt-4 font-display text-lg font-semibold text-zinc-900">Response time</h3>
                            <p className="mt-2 text-sm text-zinc-600">
                                Within one business day, Mon–Fri (SAST). Most enquiries get a same-day reply.
                            </p>
                        </div>
                    </aside>

                    <div className="lg:col-span-8">
                        {!submitted ? (
                            <form onSubmit={submit} data-testid="contact-form" className="rounded-2xl border border-zinc-200 bg-white p-7 md:p-9 shadow-sm space-y-5">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="c-name">Full name</Label>
                                        <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Smith" />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="c-email">Email</Label>
                                        <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@company.com" />
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Topic</Label>
                                    <Select value={form.topic} onValueChange={(v) => update("topic", v)}>
                                        <SelectTrigger data-testid="contact-topic"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {topics.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="c-msg">Message</Label>
                                    <Textarea id="c-msg" rows={6} data-testid="contact-message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us a little about your team and what you're hoping to do…" />
                                </div>
                                <TurnstileWidget onToken={setToken} />
                                <Button type="submit" disabled={submitting} data-testid="contact-submit-btn" className="bg-brand-primary hover:bg-brand-hover text-white h-11 px-6 w-full sm:w-auto">
                                    {submitting ? "Sending…" : <>Send message <ArrowUpRight className="ml-1 h-4 w-4" /></>}
                                </Button>
                            </form>
                        ) : (
                            <div data-testid="contact-success" className="rounded-2xl border border-emerald-200 bg-emerald-50 p-9 text-center">
                                <div className="mx-auto h-12 w-12 rounded-full bg-white flex items-center justify-center">
                                    <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                                </div>
                                <h2 className="mt-4 font-display text-2xl font-bold text-emerald-900">Message received</h2>
                                <p className="mt-2 text-sm text-emerald-800">We'll be in touch within one business day. In the meantime, feel free to <Link className="underline" to="/#roi-calculator">try the ROI calculator</Link>.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
